import { Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';

import { ClaimRequestsPartialState } from './claim-requests.reducer';
import { claimRequestsQuery } from './claim-requests.selectors';
import {
  ListClaimRequestByStatus,
  GetClaimRequest,
  CreateClaimRequest,
  EditClaimRequest,
  AssignClaimRequestToVerifier,
  ListSupervisorClaimRequestByStatus,
  ListClaimRequestByAssingee,
  ListClaimRequestByCurrentUser,
  ListClaimRequestByIdNumber,
  SearchClaimRequestByIdNumber,
  SelectClaimRequest,
  ClearClaimRequest
} from './claim-requests.actions';
import {
  CreateClaimForm,
  ClaimRequestForm,
  ClaimReq,
  FormTypes
} from '@ecosure-suite/shared/util-entities';
import { Pagination } from '@ecosure-suite/shared/utils-helpers';
import { ClaimRequestsService } from '../claim-requests.service';

@Injectable({
  providedIn: 'root'
})
export class ClaimRequestsFacade {
  loaded$ = this.store.pipe(select(claimRequestsQuery.getLoaded));
  error$ = this.store.pipe(select(claimRequestsQuery.getError));
  allClaimRequests$ = this.store.pipe(
    select(claimRequestsQuery.getAllClaimRequests)
  );
  selectedClaimRequest$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedClaimRequest)
  );
  selectedSearchByIdNumberClaimRequests$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedSearchByIdNumberClaimRequests)
  );
  selectedSearchByIdNumberClaimRequestsPage$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedSearchByIdNumberPage)
  );
  selectedListByIdNumberClaimRequests$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListByIdNumberClaimRequests)
  );
  selectedListByIdNumberClaimRequestsPage$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListByIdNumberPage)
  );
  selectedListByStatusClaimRequests$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListByStatusClaimRequests)
  );
  selectedListByStatusClaimRequestsPage$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListByStatusPage)
  );
  selectedListForSupervisorByStatusClaimRequests$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListForSupervisorByStatusClaimRequests)
  );
  selectedListForSupervisorByStatusClaimRequestsPage$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListForSupervisorByStatusPage)
  );
  selectedListByAssigneeClaimRequests$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListByAssigneeClaimRequests)
  );
  selectedListByAssigneeClaimRequestsPage$ = this.store.pipe(
    select(claimRequestsQuery.getSelectedListByAssigneePage)
  );

  constructor(private store: Store<ClaimRequestsPartialState>, private service: ClaimRequestsService) { }

  get(claimRequestId: number) {
    this.store.dispatch(new GetClaimRequest(claimRequestId));
  }

  create(claimRequest: Partial<CreateClaimForm>) {
    this.store.dispatch(new CreateClaimRequest(claimRequest));
  }

  edit(claimRequest: ClaimRequestForm) {
    this.store.dispatch(new EditClaimRequest(claimRequest));
  }

  assign(claimRquest: { claimRequestId: number; username: string }) {
    this.store.dispatch(new AssignClaimRequestToVerifier(claimRquest));
  }

  listByStatus(status, page) {
    return this.service.listByStatus(status, page);
  }

  listForSupervisorByStatus(query: { status: string; page: Pagination }) {
    this.store.dispatch(new ListSupervisorClaimRequestByStatus(query));
  }

  listByAssingee(query: { username: string; page: Pagination }) {
    this.store.dispatch(new ListClaimRequestByAssingee(query));
  }

  listByCurrentUser(page: Pagination) {
    this.store.dispatch(new ListClaimRequestByCurrentUser(page));
  }

  listByIdNumber(query: { idNumber: string; page: Pagination }) {
    this.store.dispatch(new ListClaimRequestByIdNumber(query));
  }

  searchByIdNumber(query: { idNumber: string; page: Pagination }) {
    this.store.dispatch(new SearchClaimRequestByIdNumber(query));
  }

  select(claimRequestId: number) {
    this.store.dispatch(new SelectClaimRequest(claimRequestId));
  }

  createClaim(claimReq: ClaimReq) {
    return this.service.saveClaimReq(claimReq);
  }

  editClaim(claimReq: ClaimReq, id) {
    return this.service.edit(claimReq, id);
  }

  getClaims() {
    return this.service.getClaims();
  }

  getClaimByNationalId(nationalId) {
    return this.service.getClaimByNationalId(nationalId);
  }

  getClaimDocuments(claimId) {
    return this.service.getClaimDocuments(claimId);
  }

  uploadFile(file, claimId, fileType) {
    return this.service.uploadFile(file, claimId, fileType);
  }

  deleteFile(fileId, fileType) {
    return this.service.deleteFile(fileId, fileType)
  }

  getFile(url) {
    return this.service.getFile(url);
  }

  setProcessedStatus(data: { claimId, comment, claimStage, claimStatus }) {
    return this.service.submitForApproval(data);
  }

  clear() {
    this.store.dispatch(new ClearClaimRequest());
  }
}
